// Data model + helpers for the managed-run plan-template editor. Kept separate from the
// React components so the container and PlanStepCard stay small and focused.

export const DEFAULT_OPERATOR_AGENT_VALUE = "__workflow86_managed_operator__";

// Mirrors ManagedRunOperatorTools in w86-shared-lib. `required` tools are always sent.
export const OPERATOR_TOOLS = [
  {
    toolName: "plan_tasks",
    title: "Plan tasks",
    description: "Seed and re-plan the operator's task queue.",
    required: true,
  },
  {
    toolName: "update_task_queue",
    title: "Update task queue",
    description: "Advance, complete, block, or replan tasks.",
    required: true,
  },
  {
    toolName: "schedule_message",
    title: "Schedule message",
    description: "Wake itself once at a future time.",
    required: true,
  },
  {
    toolName: "recurring_schedule",
    title: "Recurring schedule",
    description: "Wake itself on a repeating interval.",
    required: true,
  },
  {
    toolName: "edit_recurring_schedule",
    title: "Edit recurring schedule",
    description: "Change or cancel a recurring wake.",
    required: true,
  },
  {
    toolName: "list_sessions",
    title: "List sessions",
    description: "List workflow sessions for run context.",
    required: true,
  },
  {
    toolName: "get_session",
    title: "Inspect session",
    description: "Read the run, component statuses, and errors.",
    required: true,
  },
  {
    toolName: "retry_session_component",
    title: "Retry component",
    description: "Retry one failed component in the same run.",
  },
  {
    toolName: "terminate_session",
    title: "Terminate run",
    description: "Terminate a running production run after approval.",
  },
  {
    toolName: "trigger_workflow",
    title: "Trigger workflow",
    description: "Start a production run after approval.",
  },
  {
    toolName: "prepare_rerun_inputs",
    title: "Prepare rerun inputs",
    description: "Preview rerun inputs before a new run.",
  },
  {
    toolName: "rerun_session",
    title: "Rerun workflow",
    description: "Start a new run from this one after approval.",
  },
  {
    toolName: "send_email",
    title: "Send email",
    description: "Email a notification when a goal is or isn't met.",
  },
  {
    toolName: "review_task",
    title: "Create review task",
    description: "Ask a human for approval or a decision.",
    required: true,
  },
];

export const REQUIRED_OPERATOR_TOOLS = OPERATOR_TOOLS.filter(
  (tool) => tool.required
).map((tool) => tool.toolName);

export const DEFAULT_OPERATOR_TOOLS = OPERATOR_TOOLS.map((tool) => tool.toolName);

export const normalizeEnabledTools = (tools) => {
  const requested = Array.isArray(tools) ? tools : DEFAULT_OPERATOR_TOOLS;
  const allowed = new Set(DEFAULT_OPERATOR_TOOLS);
  const normalized = new Set(REQUIRED_OPERATOR_TOOLS);
  requested.forEach((toolName) => {
    if (allowed.has(toolName)) normalized.add(toolName);
  });
  return DEFAULT_OPERATOR_TOOLS.filter((toolName) => normalized.has(toolName));
};

export const DURATION_UNITS = [
  { value: "minutes", label: "Minutes" },
  { value: "hours", label: "Hours" },
  { value: "days", label: "Days" },
];

const normalizeNumber = (value, min = 1) => {
  const parsed = Number.parseInt(value, 10);
  if (!Number.isFinite(parsed)) return min;
  return Math.max(min, parsed);
};

export const durationToIso = (amount, unit) => {
  const safeAmount = normalizeNumber(amount, 1);
  if (unit === "days") return `P${safeAmount}D`;
  if (unit === "minutes") return `PT${safeAmount}M`;
  return `PT${safeAmount}H`;
};

export const parseDuration = (
  value,
  fallbackUnit = "hours",
  fallbackAmount = 1
) => {
  const text = String(value || "").trim();
  if (!text) return { amount: fallbackAmount, unit: fallbackUnit };

  let match = /^P(\d+)D$/i.exec(text);
  if (match) return { amount: Number(match[1]), unit: "days" };
  match = /^PT(\d+)H$/i.exec(text);
  if (match) return { amount: Number(match[1]), unit: "hours" };
  match = /^PT(\d+)M$/i.exec(text);
  if (match) return { amount: Number(match[1]), unit: "minutes" };

  match = /^P(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?)?$/i.exec(text);
  if (match) {
    const days = Number(match[1] || 0);
    const hours = Number(match[2] || 0);
    const minutes = Number(match[3] || 0);
    const totalMinutes = days * 24 * 60 + hours * 60 + minutes;
    if (totalMinutes > 0) {
      if (totalMinutes % (24 * 60) === 0)
        return { amount: totalMinutes / (24 * 60), unit: "days" };
      if (totalMinutes % 60 === 0)
        return { amount: totalMinutes / 60, unit: "hours" };
      return { amount: totalMinutes, unit: "minutes" };
    }
  }
  return { amount: fallbackAmount, unit: fallbackUnit, raw: text };
};

const clone = (value) => JSON.parse(JSON.stringify(value));

export const emptyStep = () => ({
  title: "",
  description: "",
  acceptanceCriteria: [],
  timeBound: true,
  deadline: "PT24H",
  checkInterval: "PT1H",
  ifMet: "",
  ifNotMet: "",
});

const defaultTasks = () => [
  {
    title: "Ensure the workflow run completes successfully",
    description:
      "Track the workflow session end to end and confirm every component reaches a successful terminal state.",
    acceptanceCriteria: [
      "The workflow session has completed with all components successful",
    ],
    timeBound: true,
    deadline: "PT24H",
    checkInterval: "PT1H",
    ifMet: "Post a brief success summary in the operator chat and mark this task complete.",
    ifNotMet:
      "Use send_email to notify the workflow owner and summarise what is still outstanding.",
  },
  {
    title: "Detect and recover component failures",
    description:
      "Watch for failed components and attempt safe recovery, escalating when recovery is not possible.",
    acceptanceCriteria: ["No component is left in a failed state"],
    timeBound: true,
    deadline: "PT24H",
    checkInterval: "PT1H",
    ifMet: "",
    ifNotMet:
      "Create a review task and, once approved, use rerun_session to retry the failed component. If it cannot be recovered, use send_email to alert the owner.",
  },
];

export const defaultPlanTemplate = () => ({
  goal:
    "Operate this production workflow run to a successful, timely completion and keep the operator chat informed.",
  tasks: defaultTasks(),
});

export const defaultPolicy = () => ({
  enabled: false,
  operatorAgentId: null,
  instructions: "",
  enabledTools: DEFAULT_OPERATOR_TOOLS,
  planTemplate: defaultPlanTemplate(),
});

// Normalizes one API/stored task into the editor shape (adds the editor-only timeBound flag).
export const normalizeStep = (task = {}) => {
  const acceptance = Array.isArray(task.acceptanceCriteria)
    ? task.acceptanceCriteria.filter((item) => String(item || "").trim())
    : [];
  const timeBound = Boolean(String(task.deadline || "").trim());
  return {
    title: String(task.title || ""),
    description: String(task.description || ""),
    acceptanceCriteria: acceptance,
    timeBound,
    deadline: timeBound ? task.deadline : "PT24H",
    checkInterval: task.checkInterval || "PT1H",
    ifMet: String(task.ifMet || ""),
    ifNotMet: String(task.ifNotMet || ""),
  };
};

// Strips editor-only fields (timeBound) and drops schedule fields for plain tasks before saving.
export const serializeStep = (task = {}) => {
  const base = {
    title: String(task.title || "").trim(),
    description: String(task.description || "").trim(),
    acceptanceCriteria: (task.acceptanceCriteria || [])
      .map((item) => String(item || "").trim())
      .filter(Boolean),
    ifMet: String(task.ifMet || "").trim(),
    ifNotMet: String(task.ifNotMet || "").trim(),
  };
  if (task.timeBound) {
    base.deadline = task.deadline || "PT24H";
    if (task.checkInterval) base.checkInterval = task.checkInterval;
  }
  return base;
};

export const normalizePolicy = (policy) => {
  if (!policy) return defaultPolicy();
  const template = policy.planTemplate || {};
  const tasks = Array.isArray(template.tasks) ? template.tasks : [];
  return {
    enabled: Boolean(policy.enabled),
    operatorAgentId: String(policy.operatorAgentId || "").trim() || null,
    instructions: String(policy.instructions || ""),
    enabledTools: normalizeEnabledTools(policy.enabledTools),
    planTemplate: {
      goal: String(template.goal || "") || defaultPlanTemplate().goal,
      tasks: tasks.length ? tasks.map(normalizeStep) : defaultPlanTemplate().tasks,
    },
  };
};

export const serializePolicy = (policy) => {
  const template = policy.planTemplate || {};
  return {
    enabled: Boolean(policy.enabled),
    operatorAgentId: policy.operatorAgentId || null,
    instructions: policy.instructions || "",
    enabledTools: normalizeEnabledTools(policy.enabledTools),
    planTemplate: {
      goal: String(template.goal || "").trim(),
      tasks: (template.tasks || []).map(serializeStep),
    },
  };
};

export const clonePolicy = clone;
